/* eslint-disable no-template-curly-in-string */
// 灵感 发现/关注/画板导航
import useAppStore from "@/context/store";
import { useSafeState } from "ahooks";
import { Tooltip } from "antd";
import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { When } from "react-if";
import HBSwiper from "../HBSwiper";
import styles from "./index.module.less";

export default function HBBoardNav({
  onChange,
  value,
  allText = "最新",
  extraOption,
  hideAll = false,
  baseHref,
  boards: externalBoards, // 外部传入的画板数据
}) {
  const { user } = useAppStore(({ user }) => ({ user }));
  const [activeOption, setActiveOption] = useSafeState(null);
  const [boards, setBoards] = useSafeState([]);
  const swiperRef = useRef(null);

  // 格式化外部传入的画板数据
  useEffect(() => {
    if (externalBoards && externalBoards.length > 0) {
      const formattedBoards = externalBoards.map((board) => ({
        value: board.board_id.toString(),
        name: board.title,
        board_id: board.board_id,
        user_id: board.user_id,
        pin_count: board.pin_count,
        follow_count: board.follow_count,
        like_count: board.like_count,
        is_private: board.is_private,
        seq: board.seq,
        pins: board.pins || [],
      }));
      setBoards(formattedBoards);
    } else {
      setBoards([]);
    }
  }, [externalBoards, setBoards]);

  useEffect(() => {
    if (value === null) {
      setActiveOption(null);
    } else if (extraOption?.value && value === extraOption?.value) {
      setActiveOption({ value: extraOption?.value, name: extraOption?.text });
    } else if (value && boards.length > 0) {
      const activeBoardOption = boards.find((option) => option.value === value);
      setActiveOption(activeBoardOption);
    } else {
      setActiveOption(undefined);
    }
  }, [value, boards, setActiveOption, extraOption]);

  const onClick = (evt, option) => {
    evt.preventDefault();
    setActiveOption(option);
    onChange && onChange(option);
  };

  const renderItem = (option, isExtra = false) => {
    const displayText = option?.name || option?.text || allText;
    const isBoard = option && option.board_id && !isExtra; // 判断是否为用户画板（有board_id的才是画板）

    // 根据不同类型设置不同的埋点名称
    const getButtonName = () => {
      if (isBoard) return "用户画板";
      if (isExtra) return option?.text || "关注"; // extraOption 通常是"关注"
      return allText || "发现"; // 默认是"发现"
    };

    const itemElement = (
      <a
        key={option?.value || "all"}
        data-gd-click="button_click"
        data-button-name={getButtonName()}
        data-module-name="页面切换tab"
        data-subject-name="按钮"
        data-board-id={option?.board_id}
        className={classNames([
          styles.item,
          isBoard ? styles.boardItem : null, // 为画板添加特殊样式
          (
            isExtra
              ? value === option?.value
              : activeOption?.value === option?.value
          )
            ? styles.active
            : null,
        ])}
        {...((
          isExtra
            ? value === option?.value
            : activeOption?.value === option?.value
        )
          ? { id: "active-filter-item" }
          : {})}
        {...(baseHref && option?.value
          ? {
              href: isExtra
                ? baseHref.replace("discovery", option?.value)
                : baseHref + "/" + option?.value,
            }
          : baseHref
          ? { href: baseHref }
          : null)}
        onClick={(evt) =>
          onClick(
            evt,
            isExtra ? { value: option?.value, name: option?.text } : option
          )
        }
      >
        <span className={isBoard ? styles.boardText : null}>{displayText}</span>
      </a>
    );

    // 只有画板项需要 tooltip，发现和关注不需要
    if (isBoard) {
      return (
        <ConditionalTooltip
          key={option?.value || "all"}
          title={displayText}
          placement="top"
          mouseEnterDelay={0.3}
        >
          {itemElement}
        </ConditionalTooltip>
      );
    }

    return itemElement;
  };

  // 只有当文本溢出时才显示 tooltip 的组件
  const ConditionalTooltip = ({ children, title, ...tooltipProps }) => {
    const [isOverflowing, setIsOverflowing] = useState(false);
    const elementRef = useRef(null);

    // 检测文本是否溢出
    const checkOverflow = useCallback(() => {
      if (elementRef.current) {
        const textSpan = elementRef.current.querySelector(
          `.${styles.boardText}`
        );
        if (textSpan) {
          setIsOverflowing(textSpan.scrollWidth > textSpan.clientWidth);
        }
      }
    }, []);

    useEffect(() => {
      const timer = setTimeout(() => {
        checkOverflow();
      }, 0); // 延迟到下一个事件循环执行，确保DOM已渲染

      // 监听窗口大小变化
      window.addEventListener("resize", checkOverflow);

      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", checkOverflow);
      };
    }, [checkOverflow, title]);

    // 克隆子元素并添加ref
    const childWithRef = React.cloneElement(children, { ref: elementRef });

    // 只有当文本溢出时才显示 tooltip
    if (isOverflowing) {
      return (
        <Tooltip title={title} {...tooltipProps}>
          {childWithRef}
        </Tooltip>
      );
    }

    return childWithRef;
  };

  const renderAllItems = () => (
    <>
      <HBSwiper
        ref={swiperRef}
        className={styles.swiper}
        iconClassName={styles.swiperIcon}
        padding={20}
      >
        <When condition={!hideAll}>
          {renderItem(null)}
          {extraOption &&
            extraOption?.text &&
            extraOption?.value &&
            renderItem(extraOption, true)}
        </When>
        {/* 只有登录用户才显示画板列表 */}
        {user && boards.map((option) => renderItem(option))}
      </HBSwiper>
    </>
  );

  // 如果用户未登录，隐藏整个导航栏
  if (!user) {
    return null;
  }

  return (
    <div className={styles["category-nav"]}>
      <div className={styles.container}>{renderAllItems()}</div>
    </div>
  );
}
